// src/worker/exit-questions.js — the exit-interview question set.
//
// ZERO DEPENDENCIES, deliberately, so the Worker, the leaver's form and the
// admin aggregate can all import the SAME catalogue. This is the BADGES
// pattern (src/worker/achievements.js): the day the form and the analysis
// disagree about what question 4 was, the trend view starts lying.
//
// Matt owns this list. Changing a LABEL is safe. Changing or removing a KEY is
// not: keys are the column headings of every interview already submitted, and
// a renamed key silently splits one question into two in the aggregate. Add
// new questions with new keys; retire old ones by leaving them here with
// retired: true so historic answers still render.
//
// The scale questions exist so there is something to TREND. Free text alone
// tells you about one leaver; five people scoring "supported by my manager" at
// 2 tells you about a store.

export const SCALE_MIN = 1;
export const SCALE_MAX = 5;

export const LEAVING_REASONS = [
  'A better offer somewhere else',
  'Pay',
  'Hours or shift pattern',
  'How I was managed',
  'Travel or location',
  'Personal or family reasons',
  'Moving away / leaving the country',
  'Health',
  'End of contract',
  'Something else',
];

export const EXIT_QUESTIONS = [
  { key: 'reason',        type: 'choice', required: true,  label: 'What is the main reason you are leaving?', options: LEAVING_REASONS },
  { key: 'reason_detail', type: 'text',   required: false, label: 'Would you like to say more about that?' },

  { key: 'work_again',    type: 'scale',  required: false, label: 'Would you work for us again?' },
  { key: 'manager',       type: 'scale',  required: false, label: 'Did you feel supported by your manager?' },
  { key: 'training',      type: 'scale',  required: false, label: 'Were you given the training you needed?' },
  { key: 'rota',          type: 'scale',  required: false, label: 'Was your rota fair and predictable?' },

  // These two are not only HR questions. A low score here is a signal for the
  // pay path and the whistleblowing path, and should be read that way.
  { key: 'pay_correct',   type: 'scale',  required: false, label: 'Were you paid correctly and on time?' },
  { key: 'could_raise',   type: 'scale',  required: false, label: 'Did you feel able to raise a concern?' },

  { key: 'one_change',    type: 'text',   required: false, label: 'What is the ONE thing we should change?' },
  { key: 'anything_else', type: 'text',   required: false, label: 'Anything else you would like to tell us?' },
];

// The scale questions, in order. The aggregate trends exactly these.
export function scaleKeys() {
  return EXIT_QUESTIONS.filter((q) => q.type === 'scale' && !q.retired).map((q) => q.key);
}

export function questionByKey(key) {
  return EXIT_QUESTIONS.find((q) => q.key === key) || null;
}

// Pure: is this a submission we can store? Returns { ok, reason }.
//
// Validates AGAINST THE CATALOGUE rather than against a shape, so a question
// removed from the list stops being accepted without anyone editing a second
// place. Unknown keys are dropped rather than rejected: a leaver halfway
// through the form when the catalogue changed should not lose their answers.
export function validateAnswers(answers) {
  if (!answers || typeof answers !== 'object') return { ok: false, reason: 'No answers were sent.' };
  for (const q of EXIT_QUESTIONS) {
    const v = answers[q.key];
    if (v === undefined || v === null || v === '') {
      if (q.required) return { ok: false, reason: 'Please answer: ' + q.label };
      continue;
    }
    if (q.type === 'scale') {
      const n = Number(v);
      if (!Number.isFinite(n) || n < SCALE_MIN || n > SCALE_MAX) {
        return { ok: false, reason: 'Please use ' + SCALE_MIN + ' to ' + SCALE_MAX + ' for: ' + q.label };
      }
    }
    if (q.type === 'choice' && q.options.indexOf(String(v)) === -1) {
      return { ok: false, reason: 'Please choose one of the options for: ' + q.label };
    }
  }
  return { ok: true };
}

// Pure: the per-interview scores, DERIVED at read time and never stored, so a
// change to this function can never silently disagree with the raw answers it
// came from (the leadership-export lesson).
export function deriveScores(answers) {
  const out = {};
  let sum = 0, n = 0;
  for (const key of scaleKeys()) {
    const v = Number(answers && answers[key]);
    if (Number.isFinite(v)) { out[key] = v; sum += v; n++; }
  }
  // An average over ZERO answers is not 0, it is nothing. Returning 0 would
  // put a leaver who skipped every scale at the bottom of every ranking.
  out.average = n ? Math.round((sum / n) * 10) / 10 : null;
  out.answered = n;
  return out;
}
